import { useState, type FormEvent } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    try {
      const formData = new FormData(form);
      if (honeypotEl) { formData.delete('website_alt'); }
      const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const response = await fetch('https://readdy.ai/api/form/d9p0m92qs38sj6jc7h5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded,
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢您的留言，我们会尽快回复您。');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查您的网络连接后重试。');
    }
  };

  return (
    <main className="pt-16">
      <div className="mx-auto px-4 md:px-6 max-w-[1280px]">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Page Header */}
        <div className="py-8 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>关于韩漫之家</h1>
          <p className="text-sm md:text-base text-foreground-500 mt-3 max-w-2xl mx-auto">
            韩漫之家致力于为广大韩漫爱好者打造最优质的在线阅读平台，让每一位读者都能便捷地获取高质量的韩漫资源。
          </p>
        </div>

        {/* Brand Story */}
        <section className="py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="rounded-lg overflow-hidden h-[300px] md:h-[360px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20space%20with%20bookshelves%20filled%20with%20Korean%20webtoon%20style%20comic%20books%2C%20soft%20natural%20light%20from%20window%2C%20comfortable%20reading%20nook%20with%20warm%20wool%20blanket%2C%20plants%20decorations%2C%20inviting%20atmosphere%2C%20editorial%20photography%20style%2C%20no%20text&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="韩漫之家品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>我们的故事</h2>
              <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                <p>
                  韩漫之家创立于2024年，源于创始团队对韩漫文化的热爱。我们发现许多韩漫爱好者面临着找资源困难、更新不及时、阅读体验不佳等问题。于是，韩漫之家应运而生——一个专注于韩漫资源聚合与在线阅读的门户平台。
                </p>
                <p>
                  经过两年多的发展，韩漫之家已经积累了超过<strong>1000部优质韩漫资源</strong>，涵盖恋爱、奇幻、动作、悬疑等多个品类，每日服务超过<strong>50万名读者</strong>，成为国内领先的韩漫在线阅读平台之一。
                </p>
                <p>
                  我们的愿景是让每一位韩漫爱好者都能通过<strong>简单的网址</strong>，轻松畅享最丰富、最新鲜的<strong>免费韩漫大全</strong>，让阅读变得简单而美好。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>我们的核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-book-read-line', title: '海量资源', desc: '超过1000部优质韩漫作品，覆盖多种类型，满足不同读者的口味需求。' },
                { icon: 'ri-flashlight-line', title: '实时更新', desc: '热门连载保持与韩国同步更新，确保读者第一时间获取最新内容。' },
                { icon: 'ri-shield-check-line', title: '品质筛选', desc: '每部作品经过严格筛选和精校翻译，保证良好的阅读体验。' },
                { icon: 'ri-smartphone-line', title: '随时随地', desc: '支持多种设备访问，简单网址即可开启阅读，无需下载。' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline / Milestones */}
        <section className="py-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>发展历程</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { year: '2024年3月', title: '韩漫之家正式上线', desc: '以聚合优质韩漫资源为核心，网站正式面向公众开放。' },
              { year: '2024年8月', title: '作品突破500部', desc: '平台韩漫资源数量快速增长，日活跃用户突破10万。' },
              { year: '2025年1月', title: '分类体系升级', desc: '推出更精细的漫画分类系统，支持按类型、热度、更新时间等多维度浏览。' },
              { year: '2025年6月', title: '日活突破30万', desc: '凭借优质内容和良好口碑，成为国内韩漫领域的重要门户。' },
              { year: '2026年1月', title: '作品突破1000部', desc: '持续扩充韩漫资源库，作品总数突破1000部里程碑。' },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-accent-500 flex-shrink-0 mt-1.5"></div>
                  {idx < 4 && <div className="w-0.5 flex-1 bg-accent-200 mt-1"></div>}
                </div>
                <div>
                  <span className="text-xs font-medium text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{item.year}</span>
                  <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                  <p className="text-sm text-foreground-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* User Testimonials */}
        <section className="py-10 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '小陈', text: '韩漫之家真的太方便了！以前找韩漫要翻好几个网站，现在一个网址就能看全部，更新也很快，五星好评！', rating: 5 },
                { name: '爱看漫画的阿琳', text: '分类很清晰，画质也好，最重要的是免费！已经推荐给身边所有喜欢看韩漫的朋友了，大家都说好用。', rating: 5 },
                { name: '夜行书生', text: '作为一个韩漫重度爱好者，韩漫之家是我每天必刷的网站。更新速度快，翻译质量高，资源也很全。', rating: 4 },
              ].map((testimonial, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-sm ${i < testimonial.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-foreground-200'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">"{testimonial.text}"</p>
                  <p className="text-xs font-medium text-foreground-800">— {testimonial.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2" style={{ fontFamily: 'var(--font-heading)' }}>联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-8">如有任何问题或建议，欢迎随时与我们联系</p>
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-background-100 rounded-lg p-4 flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">邮箱</p>
                  <p className="text-sm text-foreground-500">contact@hanmanzhijia.com</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-4 flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                  <i className="ri-feedback-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">意见反馈</p>
                  <p className="text-sm text-foreground-500">feedback@hanmanzhijia.com</p>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} data-readdy-form="" className="bg-background-50 rounded-lg border border-background-200/70 p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input id="contact-name" name="name" type="text" required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入您的姓名" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input id="contact-email" name="email" type="email" required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入您的邮箱" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input id="contact-subject" name="subject" type="text" required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入留言主题" />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea id="contact-message" name="message" rows={4} maxLength={500} required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" placeholder="请输入您的留言（最多500字）"></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>
              <input name="website_alt" type="text" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              {formStatus === 'success' && (
                <div className="flex items-center gap-2 text-sm text-accent-700 bg-accent-50 rounded-lg px-4 py-3">
                  <i className="ri-check-line"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  {formMessage}
                </div>
              )}
              <button type="submit" disabled={formStatus === 'submitting'} className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold rounded-full hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>
            </form>
          </div>
        </section>

        {/* SEO Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于韩漫之家",
          "description": "韩漫之家致力于为广大韩漫爱好者打造最优质的在线阅读平台，让每一位读者都能便捷地获取高质量的韩漫资源。",
          "url": "https://www.hanmanzhijia.com/about",
          "isPartOf": { "@type": "WebSite", "name": "韩漫之家", "url": "https://www.hanmanzhijia.com" }
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.hanmanzhijia.com" },
            { "@type": "ListItem", "position": 2, "name": "关于我们" }
          ]
        }) }} />
      </div>
    </main>
  );
}